import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import Swal from "sweetalert2";

// Import your context and API
import { useGlobalContext } from "../ContextApi/GlobalContext";
import { loginAdmin } from "../Backend/authFunction";

// Validation Schema
const schema = yup.object().shape({
  email: yup
    .string()
    .email("Invalid admin email format")
    .required("Official email is required"),
  password: yup
    .string()
    .min(6, "Min 6 characters")
    .required("Master key is required"),
});

const LoginPage = () => {
  const navigate = useNavigate();
  const [loader, setLoader] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { dispatch } = useGlobalContext();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });

  const onSubmit = async (data) => {
    setLoader(true);
    try {
      const result = await loginAdmin(data);
      if (result.success) {
        dispatch({
          authToken: result.data.token,
          companyData: result.data,
        });

        setLoader(false);

        Swal.fire({
          icon: "success",
          title: "Access Granted",
          text: "Master node initialized.",
          timer: 1500,
          showConfirmButton: false,
          customClass: { popup: "rounded-[2rem]" },
        });

        navigate("/dashboard");
      } else {
        throw new Error(result.message);
      }
    } catch (error) {
      setLoader(false);
      Swal.fire({
        icon: "error",
        title: "Access Denied",
        text: error.message || "Invalid administrative credentials",
        confirmButtonColor: "#0B1C2D",
      });
    }
  };

  return (
    <div className="min-h-screen flex bg-white font-sans selection:bg-[#E63946] selection:text-white">
      {/* 1. LEFT SIDE: COMMAND CENTER IDENTITY */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#0B1C2D] relative overflow-hidden flex-col justify-between p-16">
        {/* Glowing Decor */}
        <div className="absolute inset-0 opacity-20 pointer-events-none">
          <div className="absolute top-[-10%] right-[-10%] size-[500px] bg-[#E63946] rounded-full blur-[120px]"></div>
          <div className="absolute bottom-[-10%] left-[-10%] size-[400px] bg-blue-500 rounded-full blur-[100px] opacity-30"></div>
        </div>

        {/* Top: Brand Logo */}
        <a href="/" className="relative z-10 flex items-center gap-3 group">
          <div className="flex items-center justify-center size-10 rounded-lg bg-[#E63946] text-white transition-transform duration-300 group-hover:scale-110 shadow-lg shadow-[#E63946]/20">
            <span className="material-symbols-outlined">hub</span>
          </div>
          <span className="text-xl font-black text-white uppercase tracking-widest">
            Admin HQ
          </span>
        </a>

        {/* Center: Admin Welcome */}
        <div className="relative z-10">
          <h1 className="text-5xl font-black text-white tracking-tighter leading-[1.1] mb-6">
            Command Center <br />
            <span className="text-[#E63946]">Access Terminal.</span>
          </h1>
          <p className="max-w-md text-lg text-slate-400 font-medium leading-relaxed mb-10">
            Secure administrative entrance for distributed infrastructure
            management and organizational provisioning.
          </p>

          {/* Node Status Mockup */}
          <div className="grid grid-cols-2 gap-4 max-w-sm">
            {["Kolkata", "Bengaluru", "Delhi", "Rajasthan"].map((city) => (
              <div
                key={city}
                className="flex items-center gap-3 bg-white/5 border border-white/10 p-3 rounded-xl backdrop-blur-md"
              >
                <span className="size-1.5 rounded-full bg-green-500 animate-pulse"></span>
                <span className="text-[9px] font-black text-slate-300 uppercase tracking-widest">
                  {city} Node
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom Footer */}
        <div className="relative z-10 text-slate-500 text-[10px] font-black uppercase tracking-[0.3em]">
          © 2026 ContactHQ Infrastructure
        </div>
      </div>

      {/* 2. RIGHT SIDE: AUTHENTICATION FORM */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-slate-50 lg:bg-white">
        <div className="w-full max-w-md space-y-12 animate-fade-in-up">
          <div className="text-center lg:text-left">
            <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-[#0B1C2D] text-white mb-6 shadow-xl">
              <span className="material-symbols-outlined">verified_user</span>
            </div>
            <h2 className="text-3xl font-black text-[#0B1C2D] tracking-tighter uppercase">
              Authenticate Access
            </h2>
            <p className="mt-2 text-sm text-slate-500 font-medium">
              Provide administrative credentials to enter the HQ.
            </p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Email */}
            <div className="space-y-2 group">
              <label className="text-[10px] font-black uppercase tracking-widest text-[#0B1C2D] ml-1">
                Admin Email
              </label>
              <div className="relative">
                <span className="material-symbols-outlined absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-[#E63946] transition-colors">
                  alternate_email
                </span>
                <input
                  type="email"
                  {...register("email")}
                  className={`block w-full pl-12 pr-6 py-4 rounded-2xl bg-white border-2 ${
                    errors.email ? "border-red-300" : "border-slate-50"
                  } focus:border-[#E63946] focus:bg-white shadow-sm outline-none transition-all font-bold text-[#0B1C2D] text-sm`}
                  placeholder="admin@contacthq.in"
                />
              </div>
              {errors.email && (
                <p className="text-[10px] font-black text-red-500 ml-1">
                  {errors.email.message}
                </p>
              )}
            </div>

            {/* Password */}
            <div className="space-y-2 group">
              <div className="flex justify-between items-center ml-1">
                <label className="text-[10px] font-black uppercase tracking-widest text-[#0B1C2D]">
                  Master Access Key
                </label>
                <a
                  href="/forgot-password"
                  size="text-[10px]"
                  className="font-black text-[#E63946] uppercase hover:underline"
                >
                  Reset Key?
                </a>
              </div>
              <div className="relative">
                <span className="material-symbols-outlined absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-[#E63946] transition-colors">
                  lock
                </span>
                <input
                  type={showPassword ? "text" : "password"}
                  {...register("password")}
                  className={`block w-full pl-12 pr-14 py-4 rounded-2xl bg-white border-2 ${
                    errors.password ? "border-red-300" : "border-slate-50"
                  } focus:border-[#E63946] focus:bg-white shadow-sm outline-none transition-all font-bold text-[#0B1C2D] text-sm`}
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-[#0B1C2D]"
                >
                  <span className="material-symbols-outlined text-xl">
                    {showPassword ? "visibility_off" : "visibility"}
                  </span>
                </button>
              </div>
              {errors.password && (
                <p className="text-[10px] font-black text-red-500 ml-1">
                  {errors.password.message}
                </p>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loader}
              className="w-full flex justify-center items-center gap-3 rounded-[2rem] bg-[#0B1C2D] py-5 text-sm font-black text-white uppercase tracking-widest shadow-2xl shadow-[#0B1C2D]/20 hover:bg-[#E63946] hover:shadow-[#E63946]/30 hover:-translate-y-1 transition-all duration-300 disabled:opacity-70 group"
            >
              {loader ? (
                <div className="size-6 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <>
                  Initialize Connection
                  <span className="material-symbols-outlined text-xl group-hover:translate-x-1 transition-transform">
                    rocket_launch
                  </span>
                </>
              )}
            </button>
          </form>
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-in-up {
          animation: fadeInUp 0.8s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default LoginPage;
